/**
 * Guard de rota por papel de staff (D-10.5, Task 10).
 *
 * A Plataforma é interna: nenhuma tela monta antes de a sessão ser resolvida, e
 * sessão sem papel `operator`/`admin` não entra. Quatro estados, todos
 * distintos (nenhum se disfarça de outro):
 *
 * - `loading`: resolvendo/restaurando a sessão;
 * - `login`: não há sessão **e** o provedor sabe autenticar (Supabase) →
 *   mostra a tela de login, que é o caminho de aquisição do token;
 * - `authenticated`: sessão com papel de staff;
 * - `denied`: provedor sem login (simulador após logout) ou **produção sem
 *   configuração de auth**, que falha fechado com a mensagem do erro.
 *
 * O papel vem de claim controlada pelo servidor (ver `session.ts`); a
 * autoridade final de permissão é o backend (RLS + `profiles`, migration 0018).
 */
import {
  createContext,
  createElement as h,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode
} from "react";
import { StatusPanel } from "../components/StatusPanel.js";
import { StaffLogin } from "./StaffLogin.js";
import type { StaffSession, StaffSessionProvider } from "./session.js";

export interface StaffSessionState {
  status: "loading" | "authenticated" | "denied" | "login";
  session: StaffSession | null;
  /** Motivo da negativa/erro, exibido sem vazar detalhe de credencial. */
  reason: string | null;
  /**
   * Encerra a sessão (revoga o refresh token quando o provedor suportar) e
   * volta para a tela de login/negativa — `AppShell` é quem expõe o botão.
   * No contexto default (fora do boundary) é no-op, nunca undefined: assim
   * nenhum consumidor precisa checar presença antes de chamar.
   */
  signOut: () => void;
}

const noopSignOut = () => {};

const StaffSessionContext = createContext<StaffSessionState>({
  status: "loading",
  session: null,
  reason: null,
  signOut: noopSignOut
});

export function useStaffSession(): StaffSessionState {
  return useContext(StaffSessionContext);
}

export interface StaffSessionBoundaryProps {
  provider: StaffSessionProvider;
  children: ReactNode;
}

export function StaffSessionBoundary({ provider, children }: StaffSessionBoundaryProps): ReactElement {
  const [state, setState] = useState<StaffSessionState>({
    status: "loading",
    session: null,
    reason: null,
    signOut: noopSignOut
  });
  const [pending, setPending] = useState(false);

  /**
   * Encerra a sessão e volta para login/negativa, mesmo destino de uma sessão
   * que nunca existiu — `signOut()` do provedor já é idempotente (limpa o
   * `sessionStorage` e tenta revogar o refresh token; falha de rede não
   * reabre a sessão local, ver `SupabaseStaffSessionProvider.signOut`).
   */
  const handleSignOut = useCallback(() => {
    void provider.signOut().then(() => {
      setState(
        provider.supportsLogin
          ? { status: "login", session: null, reason: null, signOut: handleSignOut }
          : { status: "denied", session: null, reason: "Nenhuma sessão de staff ativa neste ambiente.", signOut: handleSignOut }
      );
    });
  }, [provider]);

  /** Resolve a sessão sem sessão ativa => login (se houver) ou negativa. */
  const noSessionState = useCallback(
    (reason: string | null): StaffSessionState =>
      provider.supportsLogin
        ? { status: "login", session: null, reason, signOut: handleSignOut }
        : {
            status: "denied",
            session: null,
            reason: reason ?? "Nenhuma sessão de staff ativa neste ambiente.",
            signOut: handleSignOut
          },
    [provider, handleSignOut]
  );

  useEffect(() => {
    let active = true;
    provider
      .getSession()
      .then((session) => {
        if (!active) return;
        setState(session ? { status: "authenticated", session, reason: null, signOut: handleSignOut } : noSessionState(null));
      })
      .catch((error: unknown) => {
        if (!active) return;
        // Erro ao resolver sessão NÃO vira tela de login: pode ser ambiente
        // sem configuração de auth (produção), que precisa negar explicitamente.
        setState({
          status: "denied",
          session: null,
          reason: error instanceof Error ? error.message : "falha ao validar a sessão de staff",
          signOut: handleSignOut
        });
      });
    return () => {
      active = false;
    };
  }, [provider, noSessionState, handleSignOut]);

  const handleLogin = useCallback(
    (email: string, password: string) => {
      setPending(true);
      provider
        .signIn(email, password)
        .then((session) => {
          setPending(false);
          setState(
            session
              ? { status: "authenticated", session, reason: null, signOut: handleSignOut }
              : noSessionState("Credenciais aceitas, mas a conta não tem papel de operação.")
          );
        })
        .catch((error: unknown) => {
          setPending(false);
          setState(noSessionState(error instanceof Error ? error.message : "falha ao autenticar"));
        });
    },
    [provider, noSessionState, handleSignOut]
  );

  if (state.status === "loading") {
    return h(StatusPanel, { kind: "loading", title: "Verificando sessão de staff…" });
  }
  if (state.status === "login") {
    return h(StaffLogin, { onSubmit: handleLogin, pending, error: state.reason });
  }
  if (state.status === "denied") {
    return h(StatusPanel, {
      kind: "error",
      title: "Acesso restrito à operação",
      detail: state.reason ?? "Sessão de staff não encontrada."
    });
  }
  return h(StaffSessionContext.Provider, { value: state }, children);
}
